import React, { useState, useEffect, useRef, useId } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { supabase } from './supabaseClient';
import cardbackground from './cardbackground.webp';
import buttonimage from './buttonimage.webp';

const WorksheetCard = ({ title, columnName, onClick }) => {
    const [worksheetData, setWorksheetData] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const statusRef = useRef(null);
    const notificationTimer = useRef(null);
    const fieldId = useId();
    const instructionsId = `${fieldId}-instructions`;
    const helpTextId = `${fieldId}-help`;
    const statusId = `${fieldId}-status`;
    const headingId = `${fieldId}-heading`;

    useEffect(() => {
        const fetchWorksheetData = async () => {
            try {
                setLoading(true);
                const { data: { user }, error: userError } = await supabase.auth.getUser();
                if (userError || !user) {
                    console.error('Error fetching user:', userError);
                    toast.error('User not logged in!');
                    return;
                }

                const { data, error } = await supabase
                    .schema('api')
                    .from('worksheets')
                    .select(columnName)
                    .eq('id', user.id)
                    .single();

                if (error && error.code !== 'PGRST116') { // PGRST116 means no rows found
                    console.error(`Error fetching ${title} worksheet:`, error.message);
                    if (error.message.includes('permission denied')) {
                        toast.error('Permission denied. Please check Supabase RLS settings for the worksheets table.');
                    } else {
                        toast.error(`Failed to load ${title} worksheet.`);
                    }
                    return;
                }

                if (data && data[columnName]) {
                    setWorksheetData(data[columnName]);
                }
            } catch (err) {
                console.error('Unexpected error:', err);
                toast.error('An unexpected error occurred.');
            } finally {
                setLoading(false);
            }
        };

        fetchWorksheetData();
    }, [title, columnName]);

    const handleSaveWorksheet = async () => {
        try {
            setSaving(true);
            setStatusMsg('');
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                const msg = 'User not logged in!';
                setStatusMsg(msg);
                toast.error(msg);
                return;
            }
            if (worksheetData.length > 1000) {
                const msg = 'Worksheet entry cannot exceed 1000 characters.';
                setStatusMsg(msg);
                return;
            }
            const { error } = await supabase
                .schema('api')
                .from('worksheets')
                .upsert({
                    id: user.id,
                    [columnName]: worksheetData,
                });
            if (error) {
                const msg = `Failed to save worksheet: ${error.message}`;
                setStatusMsg(msg);
                toast.error(msg);
                if (notificationTimer.current) clearTimeout(notificationTimer.current);
                notificationTimer.current = setTimeout(() => setStatusMsg(''), 4000);
                return;
            }
            const successMsg = `${title} saved successfully!`;
            setStatusMsg(successMsg);
            toast.success(successMsg);
            if (typeof onClick === 'function') {
                try { onClick(); } catch (e) { console.warn('onClick handler threw', e); }
            }
            if (statusRef.current) {
                statusRef.current.focus();
            }
            if (notificationTimer.current) clearTimeout(notificationTimer.current);
            notificationTimer.current = setTimeout(() => setStatusMsg(''), 3000);
        } catch (err) {
            setStatusMsg('An unexpected error occurred while saving worksheet.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div>Loading worksheet...</div>;

    return (
        <div
            style={{
                width: '100%',
                borderRadius: '10px',
                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                border: '2px solid black',
                backgroundImage: `url(${cardbackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: '8px',
                position: 'relative',
            }}
        >
            <div id={instructionsId} style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                This is an editable worksheet. Type your answers in the text area below. When finished, use the Save Worksheet button. You will hear a confirmation when your worksheet is saved.
            </div>
            <div id={helpTextId} style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                The worksheet can be edited freely. Use the save button after you finish making changes.
            </div>
            <div
                ref={statusRef}
                id={statusId}
                aria-live="polite"
                aria-atomic="true"
                tabIndex={-1}
                style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}
            >
                {statusMsg}
            </div>
            <h3
                id={headingId}
                style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginBottom: '4px',
                    color: '#4B5563',
                }}
            >
                {title}
            </h3>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveWorksheet();
                }}
                style={{
                    padding: '8px',
                    boxSizing: 'border-box',
                }}
            >
                <label htmlFor={fieldId} style={{ fontWeight: 'bold', fontFamily: 'Times New Roman, Times, serif', marginBottom: '4px', display: 'block' }}>
                    {title} Entry
                </label>
                <textarea
                    id={fieldId}
                    aria-labelledby={headingId}
                    aria-describedby={`${instructionsId} ${helpTextId}`}
                    aria-required="true"
                    value={worksheetData}
                    onChange={(e) => setWorksheetData(e.target.value)}
                    disabled={false}
                    style={{
                        width: '100%',
                        height: '250px',
                        padding: '8px',
                        borderRadius: '5px',
                        border: '1px solid #4B5563',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '15px',
                        resize: 'vertical',
                        backgroundColor: 'white',
                        boxSizing: 'border-box',
                    }}
                    placeholder={`Enter your ${title.toLowerCase()} (max 1000 characters)...`}
                />
                <div style={{ textAlign: 'right', marginBottom: '4px' }}>
                    <span style={{
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '14px',
                        color: worksheetData.length > 1000 ? 'red' : '#4B5563',
                    }}>
                        {worksheetData.length}/1000
                    </span>
                    <button
                        type="submit"
                        style={{
                            padding: '10px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            border: 'none',
                            borderRadius: '5px',
                            backgroundImage: `url(${buttonimage})`,
                            backgroundSize: 'cover',
                            color: 'black',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            marginBottom: '8px',
                            cursor: saving ? 'not-allowed' : 'pointer',
                            width: '150px',
                            opacity: saving ? 0.7 : 1,
                        }}
                        disabled={saving}
                        aria-label={`Save ${title} worksheet entry`}
                    >
                        {saving ? 'Saving...' : 'Save Worksheet'}
                    </button>
                </div>
                {statusMsg && (
                    <div id={statusId} role="status" aria-live="polite" style={{ marginTop: '8px', color: statusMsg.toLowerCase().includes('failed') ? 'red' : 'green', fontWeight: 'bold' }}>
                        {statusMsg}
                    </div>
                )}
            </form>
        </div>
    );
};

export default WorksheetCard;